import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Heart, User, Menu, X, WifiOff } from "lucide-react";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import Wishlist from "@/pages/Wishlist";
import Outfits from "@/pages/Outfits";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Policies from "@/pages/Policies";
import Profile from "@/pages/Profile";
import VerifyEmail from "@/pages/VerifyEmail";

// Components
import CookieConsent from "@/components/CookieConsent";
import MobileNav from "@/components/MobileNav";
import InstallPrompt from "@/components/InstallPrompt";

// Hooks
import { usePWA } from "@/hooks/usePWA";

// Use current origin so API calls always go to the same domain
// This works on preview, live (shadesense.ai), and local dev
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_69566509-b9c0-48ae-b149-6b9accd67eee/artifacts/3nmi4pqp_Dynamic%20Logo%20with%20Sapphire%20Coral%20Gradient%20%288%29.png";

import { createContext, useContext } from "react";

const AuthContext = createContext({ user: null, setUser: () => {}, isAuthenticated: false });
export const useAuth = () => useContext(AuthContext);

// Luxury Navigation Component
const Navigation = ({ user, isAuthenticated, onLogout }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="nav">
      <div className="nav-content">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src={LOGO_URL} alt="Shadesense.ai" className="h-10 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {/* Navigation links removed - access features via Profile */}
        </nav>

        {/* User Actions */}
        <div className="flex items-center gap-4">
          {isAuthenticated && user ? (
            <div className="hidden md:flex items-center gap-4">
              <button 
                onClick={() => navigate('/profile')}
                className="flex items-center gap-3 px-4 py-2 rounded-md hover:bg-black/5 transition-colors"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-accent-muted flex items-center justify-center">
                    <User className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                  </div>
                )}
                <span className="text-small" style={{ color: 'var(--text-primary)' }}>{user?.name}</span>
              </button>
              <button 
                onClick={onLogout}
                className="text-small text-secondary hover:text-primary transition-colors"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <Link 
              to="/login" 
              className="btn-primary"
            >
              Sign In
            </Link>
          )}

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden p-2" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
            ) : (
              <Menu className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--surface-primary)' }}>
          <div className="container py-4 flex flex-col gap-4">
            {isAuthenticated && user ? (
              <>
                <Link to="/profile" className="text-small py-2" style={{ color: 'var(--text-secondary)' }} onClick={() => setMobileMenuOpen(false)}>Profile</Link>
                <button onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="text-small py-2 text-left" style={{ color: 'var(--text-secondary)' }}>Sign Out</button>
              </>
            ) : (
              <Link to="/login" className="btn-primary text-center" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

// Minimal Footer Component
const Footer = () => (
  <footer className="footer">
    <div className="footer-content">
      <p className="text-meta text-muted">© {new Date().getFullYear()} Shadesense.ai — Your personal color advisor</p>
    </div>
  </footer>
);

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          // Use the same origin for API calls to avoid CORS issues
          const apiBase = window.location.origin;
          const response = await axios.post(`${apiBase}/api/auth/session`, { session_id: sessionId }, { withCredentials: true });
          
          if (response.data.success) {
            // Clear the hash from URL before navigating
            window.history.replaceState(null, '', window.location.pathname);
            // Navigate with user data in state
            navigate("/dashboard", { state: { user: response.data.user }, replace: true });
          } else {
            console.error("Auth session failed:", response.data);
            navigate("/login", { state: { error: "Authentication failed. Please try again." }, replace: true });
          }
        } catch (error) {
          console.error("Auth callback error:", error.response?.data || error.message);
          navigate("/login", { state: { error: "Authentication failed. Please try again." }, replace: true });
        }
      } else {
        // No session_id in hash, redirect to home
        navigate("/", { replace: true });
      }
    };
    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
      <div className="text-center animate-fade-in">
        <img src={LOGO_URL} alt="Shadesense.ai" className="h-14 w-auto mx-auto mb-8" />
        <div className="animate-shimmer w-48 h-1 mx-auto rounded-full mb-6"></div>
        <p className="text-meta text-muted">Authenticating...</p>
      </div>
    </div>
  );
};

// Route that allows both authenticated and guest users
const OptionalAuthRoute = ({ children }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        // Guest user - that's fine for optional auth routes
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, [location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
        <div className="animate-shimmer w-32 h-1 rounded-full"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

// Route that requires authentication
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      }
    };
    checkAuth();
  }, [location, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
        <div className="animate-shimmer w-32 h-1 rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

const SharedAnalysis = () => {
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const id = window.location.pathname.split('/').pop();
        const response = await axios.get(`${API}/shared/${id}`);
        setAnalysis(response.data.analysis);
      } catch (error) {
        console.error("Failed to load shared analysis");
      } finally {
        setIsLoading(false);
      }
    };
    fetchShared();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
        <div className="animate-shimmer w-32 h-1 rounded-full"></div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
        <div className="text-center">
          <img src={LOGO_URL} alt="Shadesense.ai" className="h-12 w-auto mx-auto mb-6" />
          <h2 className="text-h2 mb-2" style={{ color: 'var(--text-primary)' }}>Analysis not found</h2>
          <p className="text-small text-muted">This shared link may have expired</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }}>
      <div className="container py-12">
        <div className="max-w-3xl mx-auto">
          <img src={LOGO_URL} alt="Shadesense.ai" className="h-10 w-auto mb-8" />
          <h1 className="text-h1 mb-8" style={{ color: 'var(--text-primary)' }}>Shared Analysis</h1>
          <div className="space-y-6">
            {analysis.detected_items?.map((item, index) => (
              <div key={index} className="card">
                <span className="text-meta text-muted uppercase tracking-wider block mb-1">{item.category}</span>
                <h3 className="text-h3 mb-4" style={{ color: 'var(--text-primary)' }}>{item.name}</h3>
                <div className="flex flex-wrap gap-4">
                  {item.colors?.map((color, colorIndex) => (
                    <div key={colorIndex} className="flex items-center gap-3">
                      <div className="color-swatch w-12 h-12" style={{ backgroundColor: color.hex }} />
                      <div>
                        <p className="text-small font-medium" style={{ color: 'var(--text-primary)' }}>{color.pantone_name || color.name}</p>
                        <p className="text-meta text-muted">{color.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const { isOnline } = usePWA();

  // Check auth on mount and when location changes
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setAuthChecked(true);
      }
    };
    
    // If navigated with user state (from OAuth callback), use it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setAuthChecked(true);
      // Clear the state to prevent stale data on future navigations
      window.history.replaceState({}, document.title);
    } else {
      checkAuth();
    }
  }, [location.pathname, location.state]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setIsAuthenticated(false);
      setUser(null);
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Pages without navigation (landing, login, admin, legal pages)
  const noNavPages = ["/", "/login", "/admin", "/terms", "/privacy", "/cookies", "/policies"];
  const showNav = !noNavPages.includes(location.pathname) && !location.pathname.startsWith("/shared");

  return (
    <div style={{ backgroundColor: 'var(--background-primary)', minHeight: '100vh' }}>
      {/* Offline Banner */}
      {!isOnline && (
        <div className="offline-banner">
          <WifiOff className="w-4 h-4 inline-block mr-2" />
          You&apos;re offline. Some features may be unavailable.
        </div>
      )}
      
      {showNav && <Navigation user={user} isAuthenticated={isAuthenticated} onLogout={handleLogout} />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/dashboard" element={<OptionalAuthRoute><Dashboard /></OptionalAuthRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
        <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
        <Route path="/outfits" element={<ProtectedRoute><Outfits /></ProtectedRoute>} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/shared/:shareId" element={<SharedAnalysis />} />
        {/* Legal Pages */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/policies" element={<Policies />} />
      </Routes>
      {showNav && <Footer />}
      
      {/* PWA Components */}
      <MobileNav isAuthenticated={isAuthenticated} />
      <InstallPrompt />
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
